import React, { useCallback, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { authFetch } from "../../api";

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["won", "Won"], ["lost", "Lost"]];
const STATUS_STYLE = {
  new: "bg-cyan-500/15 text-cyan-300",
  contacted: "bg-amber-500/15 text-amber-300",
  won: "bg-emerald-500/15 text-emerald-300",
  lost: "bg-white/5 text-gray-500",
};

// The quote requests visitors send from the system pages (e.g. mraaliERP) and the service pages.
export default function QuoteRequestsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    authFetch("/core/quote-requests/").then((r) => r.json()).then(setItems).catch(() => setError("Failed to load the quote requests."));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (item, status) => {
    const res = await authFetch(`/core/quote-requests/${item.id}/`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) setItems((list) => list.map((q) => (q.id === item.id ? { ...q, status } : q)));
    else setError("Could not change the status.");
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete the request of ${item.name}?`)) return;
    await authFetch(`/core/quote-requests/${item.id}/`, { method: "DELETE" });
    load();
  };

  const shown = items.filter((q) => filter === "all" || q.status === filter);
  const count = (status) => items.filter((q) => q.status === status).length;

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Quote Requests</h1>
      <p className="text-sm text-gray-400 mb-6">What visitors ask for on the system pages and the service pages. Move each one along as you follow it up.</p>
      {error && <p className="text-red-400 mb-4">{error}</p>}

      <div className="flex flex-wrap gap-2 mb-4">
        {[["all", `All (${items.length})`], ...STATUSES.map(([key, label]) => [key, `${label} (${count(key)})`])].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === key ? "border-accent-light text-accent-light bg-accent-light/10" : "border-navy-border text-gray-400 hover:text-white"}`}>{label}</button>
        ))}
      </div>

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3">Who</th>
              <th className="px-4 py-3">Asked about</th>
              <th className="px-4 py-3">Price seen</th>
              <th className="px-4 py-3">Sent</th>
              <th className="px-4 py-3 w-36">Status</th>
              <th className="px-4 py-3 w-12" />
            </tr>
          </thead>
          <tbody>
            {shown.map((q) => (
              <React.Fragment key={q.id}>
                <tr className="border-t border-navy-border align-top cursor-pointer hover:bg-white/[0.03]" onClick={() => setOpen(open === q.id ? null : q.id)}>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{q.name}</div>
                    <div className="text-xs text-gray-400">{q.company && `${q.company} · `}<a href={`mailto:${q.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-accent-light">{q.email}</a></div>
                    {q.phone && <div className="text-xs text-gray-500">{q.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{q.service || "General"}{q.package_name && <div className="text-xs text-gray-500">{q.package_name} plan</div>}</td>
                  <td className="px-4 py-3 text-gray-300">{q.quoted_price || "-"}</td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select value={q.status} onChange={(e) => setStatus(q, e.target.value)} aria-label={`Status of ${q.name}`}
                      className={`px-2 py-1 rounded-full text-xs font-semibold border-0 ${STATUS_STYLE[q.status]}`}>
                      {STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => remove(q)} aria-label={`Delete the request of ${q.name}`} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"><FiTrash2 size={14} /></button>
                  </td>
                </tr>
                {open === q.id && (
                  <tr className="bg-white/[0.02]"><td colSpan={6} className="px-4 py-3 text-gray-300 whitespace-pre-wrap">{q.message || <span className="text-gray-500">No message.</span>}</td></tr>
                )}
              </React.Fragment>
            ))}
            {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No quote requests here.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
